import { type FC, Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Loader2, MessageSquareWarningIcon, Save } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ZodTypeAny, z } from 'zod';
import { type FieldProps, renderField } from '@/components/inspector/entity-fields/form/field-components';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { requestUploadSlot, updateVariant, uploadWatermarkFile } from '@/services/variant.service';
import { type BackendError, useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useVariant } from '@/hooks/variants/useVariant';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { type Option } from '@/types/select-option';
import { Button } from '@/components/ui/button';
import { FileType } from '@/types/fileType';
import { QueryKey } from '@/lib/queryKeys';
import { log } from '@/utilities/log';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export interface UpdateVariantPayload {
  name: any;
  description: any;
  fileType: any;
  versionTypeFilter: any;
  automatic: boolean;
  public: any;
  options: Record<string, any>;
  watermarkUploadId?: string;
  watermark?: any;
  auditable: boolean;
}

export interface FieldProp {
  name: string;
  slug: string;
  description?: string | ReactNode;
  type: string;
  value: any;
  validation?: ZodTypeAny;
  options?: Array<Option>;
  readOnly?: string;
  disabled?: boolean;
}

export interface AccordionProps {
  id: string;
  label: string;
  fields: Array<FieldProp>;
  readOnly?: string;
  disabled?: boolean;
}

export const VariantFields: FC<{ variantId: string; enabled: boolean }> = ({ variantId, enabled }) => {
  const [currentAccordionItems, setCurrentAccordionItems] = useState<Array<string>>([
    'variant',
    'resize',
    'watermark',
    'video',
    'image',
  ]);
  const [lastVariantId, setLastVariantId] = useState<string | null>(null);

  const tCommon = useTranslations('common');
  const tVariantFields = useTranslations('inspector.variantFields');
  const tToasts = useTranslations('toasts');
  const tValidation = useTranslations('validation');
  const queryClient = useQueryClient();

  const {
    variant: fieldData,
    variantIsFetching,
    variantIsPending,
  } = useVariant(variantId, {
    enabled: enabled,
  });
  const { toast } = useToast();

  const formFields: Array<AccordionProps> = useMemo(() => {
    if (!fieldData) {
      return [];
    }

    return [
      {
        id: 'variant',
        label: 'Variant',
        fields: [
          {
            name: 'Variant Name',
            slug: 'name',
            type: 'string',
            value: fieldData.name ?? '',
            validation: z
              .string({
                error: (issue) => {
                  if (!issue.input) {
                    return tValidation('fieldRequired', { field: tValidation('fields.name') });
                  }
                },
              })
              .min(1, tValidation('fieldRequired', { field: tValidation('fields.name') }))
              .max(255, tValidation('fieldMaxLength', { field: tValidation('fields.name'), max: 255 }))
              .regex(/[a-zA-Z]/, tValidation('nameMustContainLetter')),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Slug',
            slug: 'slug',
            type: 'string',
            value: fieldData.slug ?? '',
            readOnly: 'readonly',
            disabled: true,
            isLoading: variantIsFetching,
          },
          {
            name: 'Description',
            slug: 'description',
            type: 'text',
            value: fieldData.description ?? '',
            validation: z
              .string({
                error: (issue) => {
                  if (issue.input === undefined) {
                    return tValidation('fieldMustBeString', { field: tValidation('fields.descriptionField') });
                  }
                },
              })
              .nullable(),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Applies to',
            description: 'This is the type of files this variant should be generated from.',
            slug: 'versionTypeFilter',
            type: 'multi_select',
            value: fieldData.versionTypeFilter,
            options: [
              {
                label: 'Image',
                value: 'image',
              },
              {
                label: 'Video',
                value: 'video',
              },
              {
                label: 'Document',
                value: 'document',
              },
            ],
            validation: z.array(z.enum(['image', 'video', 'document'])).nonempty(tValidation('nonempty')),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Variant type',
            description: (
              <>
                {tVariantFields('typeDescription')}
                <br />
                <br />
                {tVariantFields('typeImmutableNote')}
              </>
            ),
            slug: 'fileType',
            type: 'select',
            value: fieldData.fileType ?? 'image',
            options: [
              { label: 'Image', value: 'image' },
              { label: 'Video', value: 'video' },
            ],
            validation: z.enum(['image', 'video']),
            disabled: true,
            isLoading: variantIsFetching,
          },
          {
            name: 'Automatic',
            description:
              'When this option is selected, the variant will be generated automatically for every new asset or asset version.',
            slug: 'automatic',
            type: 'boolean',
            value: true,
            readOnly: 'readonly',
            disabled: true,
            validation: z.boolean().optional(),
            isLoading: variantIsFetching,
          },
          {
            name: 'Auditable',
            description: '',
            slug: 'auditable',
            type: 'boolean',
            value: fieldData.auditable ?? false,
            validation: z.boolean(),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
        ],
      },
      {
        id: 'image',
        label: 'Format',
        fields: [
          {
            name: 'Format',
            slug: 'options.format',
            type: 'select',
            value: fieldData.options?.format ?? 'png',
            description:
              'This is the file format that is to be generated by this variant. The choices here are dependant on the selected variant type.',
            options: [
              { label: 'JPG', value: 'jpg' },
              { label: 'PNG', value: 'png' },
              { label: 'TIFF', value: 'tiff' },
            ],
            // validation: z.enum(['jpg', 'png', 'tiff']).optional(),
            validation: z.string(),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Quality',
            slug: 'options.quality',
            type: 'quality_slider',
            value: fieldData.options?.quality !== undefined ? [fieldData.options.quality] : [80],
            description: 'JPG encoding quality.',
            min: 1,
            max: 100,
            step: 1,
            validation: z.array(z.number().min(1).max(100)).optional(),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
        ],
      },
      {
        id: 'video',
        label: 'Format',
        fields: [
          {
            name: 'Format',
            slug: 'options.format',
            type: 'select',
            value: fieldData.options?.format ?? 'mp4',
            description:
              'This is the file format that is to be generated by this variant. The choices here are dependant on the selected variant type.',
            options: [{ label: 'MP4', value: 'mp4' }],
            // validation: z.enum(['mp4']).optional(),
            validation: z.string().optional(),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Bitrate (Mbps)',
            slug: 'options.bitrate',
            type: 'float',
            value: fieldData.options?.bitrate ? fieldData.options.bitrate / 1_000_000 : '',
            min: 1,
            validation: z.preprocess(
              (val) => {
                if (typeof val === 'string' && val.trim() === '') {
                  return undefined;
                }
                return typeof val === 'string' ? parseFloat(val) : val;
              },
              z
                .number({
                  error: (issue) => {
                    if (issue.code === 'invalid_type') {
                      return tValidation('validNumber');
                    }
                  },
                })
                .min(0.001, tValidation('valueMustBeGreaterThanZero'))
                .optional(),
            ),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Framerate',
            slug: 'options.framerate',
            type: 'float',
            value: fieldData.options?.framerate ?? '',
            min: 1,
            validation: z.preprocess(
              (val) => {
                if (typeof val === 'string' && val.trim() === '') {
                  return undefined;
                }
                return typeof val === 'string' ? parseFloat(val) : val;
              },
              z
                .number({
                  error: (issue) => {
                    if (issue.code === 'invalid_type') {
                      return tValidation('validNumber');
                    }
                  },
                })
                .min(0.001, tValidation('valueMustBeGreaterThanZero'))
                .optional(),
            ),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Audio Codec',
            slug: 'options.audioCodec',
            type: 'select',
            value: fieldData.options?.audioCodec,
            options: [
              { label: 'AAC', value: 'aac' },
              { label: 'AAC-HE', value: 'aac-he' },
              { label: 'AAC-HE-V2', value: 'aac-he-v2' },
              { label: 'MP3', value: 'mp3' },
              { label: 'AC3', value: 'ac3' },
              { label: 'EAC3', value: 'eac3' },
            ],
            validation: z.string().optional(),
            // validation: z.enum(['aac', 'aac-he', 'aac-he-v2', 'mp3', 'ac3', 'eac3']).optional(),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Audio Bitrate (kbps)',
            slug: 'options.audioBitrate',
            type: 'float',
            value: fieldData.options?.audioBitrate ? fieldData.options.audioBitrate / 1_000 : '128',
            min: 1,
            validation: z.preprocess(
              (val) => {
                if (typeof val === 'string' && val.trim() === '') {
                  return undefined;
                }
                return typeof val === 'string' ? parseFloat(val) : val;
              },
              z
                .number({
                  error: (issue) => {
                    if (issue.code === 'invalid_type') {
                      return tValidation('validNumber');
                    }
                  },
                })
                .min(0.001, tValidation('valueMustBeGreaterThanZero'))
                .optional(),
            ),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
        ],
      },
      {
        id: 'resize',
        label: 'Resize',
        fields: [
          {
            name: 'Resize Mode',
            description: 'Determines how the image is resized by this variant.',
            slug: 'resize',
            type: 'select',
            value: fieldData.options?.resize ?? 'original',
            options: [
              { label: 'Original', value: 'original' },
              { label: 'Cover', value: 'cover' },
              { label: 'Contain', value: 'contain' },
              { label: 'Crop', value: 'crop' },
            ],
            validation: z.enum(['original', 'cover', 'contain', 'crop']),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Width',
            description: 'The width of the resized image.',
            slug: 'width',
            type: 'float',
            min: 0,
            value: fieldData?.options?.width ?? 0,
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
          {
            name: 'Height',
            description: 'The height of the resized image.',
            slug: 'height',
            type: 'float',
            min: 0,
            value: fieldData?.options?.height ?? 0,
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
        ],
      },
      {
        id: 'watermark',
        label: 'Watermark',
        fields: [
          {
            name: 'Watermark',
            slug: 'watermark',
            type: 'dropzone',
            value: fieldData.watermark ?? null,
            noLabel: true,
            validation: z.any().optional(),
            disabled: variantIsFetching,
            isLoading: variantIsFetching,
          },
        ],
      },
    ];
  }, [fieldData, tValidation, tVariantFields, variantIsFetching]);

  const formSchema = useMemo(() => {
    const shape: Record<string, ZodTypeAny> = {};
    formFields.forEach((accordion) => {
      accordion.fields.forEach((field) => {
        if (field.slug.includes('.')) {
          const [parent, child] = field.slug.split('.');
          if (!shape[parent]) {
            shape[parent] = z.object({ [child]: field.validation ?? z.any() });
          } else {
            shape[parent] = (shape[parent] as z.ZodObject<any>).extend({
              [child]: field.validation ?? z.any(),
            });
          }
        } else {
          shape[field.slug] = field.validation ?? z.any();
        }
      });
    });
    return z.object(shape).superRefine((data, ctx) => {
      const resizeMode = data.resize as string | undefined;
      if (resizeMode === 'contain' || resizeMode === 'cover' || resizeMode === 'crop') {
        const width = data.width as number | undefined;
        const height = data.height as number | undefined;
        if (!width || width <= 0) {
          ctx.addIssue({
            code: 'custom',
            message: tValidation('fieldRequired', { field: tValidation('fields.width') }),
            path: ['width'],
          });
        }
        if (!height || height <= 0) {
          ctx.addIssue({
            code: 'custom',
            message: tValidation('fieldRequired', { field: tValidation('fields.height') }),
            path: ['height'],
          });
        }
      }

      // Make video fields required when fileType is 'video'
      if (data.fileType === 'video') {
        const options = data.options as Record<string, any> | undefined;
        if (!options?.bitrate || options.bitrate === '') {
          ctx.addIssue({
            code: 'custom',
            message: tValidation('fieldRequiredForVideoVariants', { field: tValidation('fields.bitrate') }),
            path: ['options', 'bitrate'],
          });
        }

        if (!options?.framerate || options.framerate === '') {
          ctx.addIssue({
            code: 'custom',
            message: tValidation('fieldRequiredForVideoVariants', { field: tValidation('fields.framerate') }),
            path: ['options', 'framerate'],
          });
        }

        if (!options?.audioCodec || options.audioCodec === '') {
          ctx.addIssue({
            code: 'custom',
            message: tValidation('fieldRequiredForVideoVariants', { field: tValidation('fields.audioCodec') }),
            path: ['options', 'audioCodec'],
          });
        }

        if (!options?.audioBitrate || options.audioBitrate === '') {
          ctx.addIssue({
            code: 'custom',
            message: tValidation('fieldRequiredForVideoVariants', { field: tValidation('fields.audioBitrate') }),
            path: ['options', 'audioBitrate'],
          });
        }
      }
    });
  }, [formFields, tValidation]);

  const defaultValues = useMemo(() => {
    const values: Record<string, any> = {};
    formFields.forEach((accordion) => {
      accordion.fields.forEach((field) => {
        if (field.slug.includes('.')) {
          const [parent, child] = field.slug.split('.');
          values[parent] ??= {};
          values[parent][child] = field.value;
        } else {
          values[field.slug] = field.value;
        }
      });
    });
    return values;
  }, [formFields]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });
  const { watch, handleSubmit, reset, setError, getValues } = form;
  const { handleError } = useFormErrorHandler(setError, getValues);

  const watchedFileType = watch('fileType');
  const watchedResize = watch('resize');
  const watchedFormatImage = watch('options.format');

  const requestUploadSlotWithAuth = useAuthenticatedQueryFn(requestUploadSlot);
  const updateVariantWithAuth = useAuthenticatedQueryFn(updateVariant);

  const updateVariantMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => updateVariantWithAuth({ id, body: payload }),
    onSuccess: () => {
      toast({
        title: tToasts('updateVariantMutation.title'),
        description: tToasts('updateVariantMutation.description'),
      });

      reset(getValues());

      void queryClient.invalidateQueries({ queryKey: [QueryKey.VariantList] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Variant, variantId] });
    },
    onError: (err: BackendError) => {
      handleError(err);
      log(err);
      toast({
        title: tCommon('error'),
        description: err?.message || tToasts('updateVariantMutation.errorDescription'),
      });
    },
  });

  async function onSubmit(values: any) {
    const {
      name,
      description,
      versionTypeFilter,
      fileType,
      options: { format, quality, bitrate, framerate, audioCodec, audioBitrate },
      watermark,
      resize,
      width,
      height,
      auditable,
    } = values;

    let watermarkUploadId = fieldData.watermarkUploadId;

    try {
      if (watermark === null) {
        // User removed the watermark
        watermarkUploadId = undefined;
      } else if (watermark && watermark !== fieldData.watermark) {
        // New watermark was added
        const { id, url } = await requestUploadSlotWithAuth({});
        await uploadWatermarkFile(url, watermark);
        watermarkUploadId = id;
      }

      // If fileType === FileType.Image, we pick the image fields
      // If fileType === FileType.Video, we pick the video fields
      const options: Record<string, any> = {};
      if (fileType === FileType.Image) {
        // Quick fix for the format validation, but this will have to be done properly.
        if (format === 'mp4') {
          options.format = 'png';
        } else {
          options.format = format;
        }
        options.quality = quality?.length && quality[0];
      } else if (fileType === FileType.Video) {
        options.format = format;
        options.bitrate =
          typeof bitrate === 'string'
            ? parseFloat(bitrate) * 1_000_000
            : typeof bitrate === 'number'
              ? bitrate * 1_000_000
              : undefined;
        options.framerate = framerate;
        options.audioCodec = audioCodec;
        options.audioBitrate =
          typeof audioBitrate === 'string'
            ? parseFloat(audioBitrate) * 1_000
            : typeof audioBitrate === 'number'
              ? audioBitrate * 1_000
              : undefined;
      }

      options.resize = resize;

      if (resize && (resize === 'contain' || resize === 'cover' || resize === 'crop')) {
        options.width = width;
        options.height = height;
      }

      const payload: UpdateVariantPayload = {
        name,
        description,
        fileType,
        versionTypeFilter,
        automatic: true,
        public: false,
        auditable,
        options,
      };

      if (watermarkUploadId) {
        payload.watermarkUploadId = watermarkUploadId;
      }

      // If user removes the watermark send null
      if (watermark === null) {
        payload.watermark = null;
      }

      updateVariantMutation.mutate({ id: variantId, payload });
    } catch (err: any) {
      toast({
        title: tCommon('error'),
        description: err?.message ?? tToasts('updateVariantMutation.processRequestErrorDescription'),
      });
    }
  }

  useEffect(() => {
    if (!variantIsFetching && fieldData && variantId !== lastVariantId) {
      setLastVariantId(variantId);

      const newDefaults: Record<string, any> = {};
      formFields.forEach((acc) => {
        acc.fields.forEach((f) => {
          newDefaults[f.slug] = f.value;
        });
      });
      reset(newDefaults);
    }
  }, [variantIsFetching, fieldData, variantId, lastVariantId, formFields, reset]);

  const isLoading = updateVariantMutation.isPending;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Accordion
          onValueChange={(accordion) => setCurrentAccordionItems(accordion)}
          value={currentAccordionItems}
          type="multiple"
          className="w-full"
        >
          {variantIsPending ? (
            <AccordionItem value="variant" key="variant">
              <AccordionTrigger>
                <Skeleton className="h-6 w-1/2" />
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col space-y-6">
                  {Array.from(Array(3)).map((_, index) => (
                    <Fragment key={`skeleton-${index}`}>
                      <div className="flex flex-col space-y-3">
                        <Skeleton className={cn('h-5 w-1/3', { 'w-2/5': index === 0 })} />
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className={cn('h-5 w-1/3', { 'w-2/3': index === 1 })} />
                      </div>
                      {index !== 2 && <Separator className="block" />}
                    </Fragment>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ) : (
            formFields
              .filter((accordion) =>
                watchedFileType === FileType.Image ? accordion.id !== FileType.Video : accordion.id !== FileType.Image,
              )
              .map((accordion) => (
                <AccordionItem key={accordion.id} value={accordion.id}>
                  <AccordionTrigger>{accordion.label}</AccordionTrigger>
                  <AccordionContent className="relative p-0">
                    {accordion.id === 'variant' && (
                      <div
                        data-cy="variant-warning"
                        className="flex flex-col gap-y-3 bg-status-warning px-3 py-5 text-text-base dark:text-text-inverted"
                      >
                        <MessageSquareWarningIcon className="icon-6" />
                        {tVariantFields('newUploadsOnlyWarning')}
                      </div>
                    )}
                    <div className="flex flex-col space-y-6 px-3 py-5">
                      {watchedFileType === FileType.Image && accordion.id === FileType.Image
                        ? formFields
                            .filter((a) => a.id === FileType.Image)
                            .flatMap((a) => a.fields as Array<FieldProps>)
                            .map((field) => {
                              // Hide quality slider if image format is not jpg
                              if (field.slug === 'options.quality' && watchedFormatImage !== 'jpg') {
                                return null;
                              }

                              return renderField(form, field);
                            })
                        : watchedFileType === FileType.Video && accordion.id === FileType.Video
                          ? formFields
                              .filter((a) => a.id === FileType.Video)
                              .flatMap((a) => a.fields as Array<FieldProps>)
                              .map((field) => {
                                // Make these fields required when fileType is video
                                const isVideoRequiredField = [
                                  'options.bitrate',
                                  'options.framerate',
                                  'options.audioCodec',
                                  'options.audioBitrate',
                                ].includes(field.slug);

                                return renderField(form, {
                                  ...field,
                                  isRequired: watchedFileType === 'video' && isVideoRequiredField,
                                });
                              })
                          : accordion.fields.map((field: any) => {
                              if ((field.slug === 'width' || field.slug === 'height') && watchedResize === 'original') {
                                return null;
                              }
                              return renderField(form, {
                                ...field,
                                ...((field.slug === 'width' || field.slug === 'height') && {
                                  isRequired: watchedResize !== 'original',
                                }),
                              });
                            })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))
          )}
        </Accordion>
        <div className="flex justify-center bg-ui-panel p-3">
          <Button
            disabled={variantIsFetching || isLoading || !form.formState.isDirty}
            type="submit"
            variant="navigation-action-primary"
            className="w-full justify-center focus-visible:ring-offset-ui-panel"
            data-cy="variant-save-button"
            iconClassName={cn('icon-4!', { 'animate-spin': isLoading })}
            icon={isLoading ? Loader2 : Save}
          >
            {isLoading ? tCommon('saving') : tCommon('save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
